import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Source, WebSource, SpreadsheetSource, Figure, Paragraph, Table, CitationPart } from '../types';
import SourcePopover from 'src/components/ui/SourcePopover';
import { useFigureContext } from '../lib/FigureContext';
import { GlobeSimpleIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { usePostHog } from 'src/lib/posthog';
import { apiGet } from 'src/lib/api/axios';

/** Same fixed panel as resource-link previews, so the card does not change width as the excerpt arrives. */
const CITATION_PANEL_CLASS = 'w-[340px] max-w-[min(340px,90vw)]';

function CitationBodyPlaceholder({ failed }: { failed: boolean }) {
  if (failed) {
    return <div className="mt-2 text-xs text-content-subtle">Preview unavailable</div>;
  }
  return (
    <div className="mt-2" aria-busy="true" aria-label="Loading excerpt">
      <div className="h-4 bg-gray-100 rounded animate-pulse mb-2 w-3/4" />
      <div className="h-3 bg-gray-100 rounded animate-pulse mb-1 w-1/2" />
      <div className="h-3 bg-gray-100 rounded animate-pulse mb-1 w-1/3" />
    </div>
  );
}

type CitationBody = (Paragraph | Figure | Table)[];

const citationBodyCache = new Map<string, CitationPart>();
const citationBodyInflight = new Map<string, Promise<CitationPart | null>>();

function loadCitationBody(sessionId: string, partId: string): Promise<CitationPart | null> {
  const cached = citationBodyCache.get(partId);
  if (cached) return Promise.resolve(cached);
  const existing = citationBodyInflight.get(partId);
  if (existing) return existing;
  const promise = apiGet<{ citation: CitationPart }>(`chat/chats/${sessionId}/citations/${partId}`, {
    meta: { globalError: false, context: 'citation-preview', background: true }
  })
    .then((response) => {
      citationBodyCache.set(partId, response.data.citation);
      return response.data.citation;
    })
    .catch(() => null)
    .finally(() => {
      citationBodyInflight.delete(partId);
    });
  citationBodyInflight.set(partId, promise);
  return promise;
}

/** Outcome of a figure's SAS-link fetch. Absent means "not attempted yet". */
type FigureState = { status: 'ready'; url: string } | { status: 'unavailable' };

const format_sources = (source: Source, figureStates: Map<string, FigureState>) => {
  // Flatten all quote line IDs into a single set for fast lookup
  const quotedLineIds = new Set(source.quote || []);

  // First, flatten all lines from paragraph sources to find the range to show
  const allLines: Array<{ line: any; sourceIndex: number; lineIndex: number }> = [];
  source.sources.forEach((src, sourceIndex) => {
    if (src.type === 'paragraph') {
      src.lines.forEach((line, lineIndex) => {
        allLines.push({ line, sourceIndex, lineIndex });
      });
    }
  });

  // Find the indices of highlighted lines
  const highlightedIndices = allLines
    .map((item, index) => (quotedLineIds.has(item.line.id) ? index : -1))
    .filter((index) => index !== -1);

  // Calculate the range to show (5 lines before first highlight to 5 lines after last highlight)
  let startIndex = 0;
  let endIndex = allLines.length;

  if (highlightedIndices.length > 0) {
    const firstHighlightIndex = Math.min(...highlightedIndices);
    const lastHighlightIndex = Math.max(...highlightedIndices);
    startIndex = Math.max(0, firstHighlightIndex - 5);
    endIndex = Math.min(allLines.length, lastHighlightIndex + 6);
  }

  // Get the lines to show
  const linesToShow = allLines.slice(startIndex, endIndex);
  const lineIdsToShow = new Set(linesToShow.map((item) => item.line.id));

  return source.sources
    .map((src) => {
      if (src.type === 'paragraph') {
        // Only include lines that are in our filtered range
        const filteredLines = src.lines.filter((line) => lineIdsToShow.has(line.id));

        const renderedLines: string[] = [];
        let highlightedBlock: string[] = [];
        const flushHighlightedBlock = () => {
          if (highlightedBlock.length === 0) return;
          renderedLines.push(`<span class="bg-yellow-100">${highlightedBlock.join('\n')}</span>`);
          highlightedBlock = [];
        };

        filteredLines.forEach((line) => {
          if (quotedLineIds.has(line.id)) {
            highlightedBlock.push(line.text);
          } else {
            flushHighlightedBlock();
            renderedLines.push(line.text);
          }
        });
        flushHighlightedBlock();

        // Only return if there are lines to show
        return renderedLines.length > 0 ? renderedLines.join('\n') : null;
      } else if (src.type === 'figure') {
        const figureState = figureStates.get(`${source.external_id}-${src.figure_id}`);
        const caption = src.caption ? `*${src.caption}*` : '';

        if (figureState === undefined) {
          return `*Loading figure ${src.figure_id}...*`;
        }
        if (figureState.status === 'unavailable') {
          // The link was refused or the blob has gone. Fall back to the caption
          // so the cited text still reads, rather than leaving a placeholder
          // that never resolves.
          return caption || '*Figure*';
        }
        return `![Figure ${src.figure_id}](${figureState.url})${caption ? `\n\n${caption}` : ''}`;
      } else if (src.type === 'table') {
        const caption = src.caption ? `*${src.caption}*\n\n` : '';
        return `${caption}${src.content}`;
      }
      return null;
    })
    .filter((item) => item !== null)
    .join('\n\n');
};

export function Citation({
  data,
  onViewSourceClick,
  hideTitle = false
}: {
  data: Source;
  onViewSourceClick: (source: Source) => void;
  hideTitle?: boolean;
}) {
  const posthog = usePostHog();
  const { sessionId } = useParams();
  const { getSasUrl } = useFigureContext();
  const [figureStates, setFigureStates] = useState<Map<string, FigureState>>(new Map());
  const [preview, setPreview] = useState<CitationPart | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const bodySources: CitationBody = preview?.sources ?? data.sources ?? [];

  // Deferred to popover open rather than mount, matching ActionSourceTooltip: a
  // figure is only ever visible inside the popover, and its link is authorised
  // against the cited document, which the reader may not hold read on (sharing
  // a chat grants nothing on its citation sources). Fetching on mount turns
  // that refusal into a burst of failed requests behind every citation the
  // reader never opens. Resolved links come back from the FigureContext cache,
  // so re-opening a popover costs nothing.
  const loadFigures = useCallback(
    async (sources: CitationBody) => {
      if (!data.external_id || sources.length === 0) return;

      const figuresToLoad: Figure[] = sources.filter((src) => src.type === 'figure') as Figure[];

      for (const figure of figuresToLoad) {
        const figureKey = `${data.external_id}-${figure.figure_id}`;
        try {
          const url = await getSasUrl(figureKey);
          setFigureStates((prev) => new Map(prev).set(figureKey, { status: 'ready', url }));
        } catch (error) {
          console.error('Failed to load figure:', error);
          setFigureStates((prev) => new Map(prev).set(figureKey, { status: 'unavailable' }));
        }
      }
    },
    [data.external_id, getSasUrl]
  );

  const previewSource = useMemo(
    (): Source =>
      preview ? { ...data, sources: preview.sources ?? data.sources, quote: preview.quotes?.[0] ?? data.quote } : data,
    [data, preview]
  );

  const highlightedText = useMemo(() => {
    const formatted = format_sources(previewSource, figureStates);
    if (!formatted) return formatted;

    const trimmed = formatted.trimEnd();
    const strippedEnding = trimmed.replace(/[\s"'`)\]*_]+$/g, '');
    const hasTerminatingCharacter = /[.!?…:]$/.test(strippedEnding);

    return hasTerminatingCharacter ? formatted : `${trimmed}...`;
  }, [previewSource, figureStates]);

  const pillPage = data.page ?? bodySources[0]?.page.printed_page_number ?? bodySources[0]?.page.page_index;
  const firstBodyPage = bodySources[0]?.page.printed_page_number ?? bodySources[0]?.page.page_index;
  const lastBodyPage =
    bodySources[bodySources.length - 1]?.page.printed_page_number ??
    bodySources[bodySources.length - 1]?.page.page_index;
  const pageInfo = firstBodyPage
    ? `Page ${firstBodyPage}${firstBodyPage !== lastBodyPage ? `-${lastBodyPage}` : ''}`
    : pillPage
      ? `Page ${pillPage}`
      : undefined;

  const prefetchPreview = useCallback(() => {
    if (!sessionId || !data.part_id || preview || previewFailed || bodySources.length > 0) return;
    void loadCitationBody(sessionId, data.part_id).then((citation) => {
      if (citation) setPreview(citation);
      else setPreviewFailed(true);
    });
  }, [sessionId, data.part_id, preview, previewFailed, bodySources.length]);

  const loadPreview = useCallback(async () => {
    let sources = bodySources;
    if (sources.length === 0 && sessionId && data.part_id && !preview && !previewFailed) {
      const citation = await loadCitationBody(sessionId, data.part_id);
      if (citation) {
        sources = citation.sources ?? [];
        setPreview(citation);
      } else {
        setPreviewFailed(true);
      }
    }
    await loadFigures(sources);
  }, [bodySources, sessionId, data.part_id, loadFigures, preview, previewFailed]);

  const handleViewSourceClick = () => {
    posthog?.capture('chat_footnote_clicked');
    onViewSourceClick(data);
  };

  return (
    <>
      <SourcePopover
        content={highlightedText}
        contentPlaceholder={
          data.part_id && bodySources.length === 0 ? <CitationBodyPlaceholder failed={previewFailed} /> : undefined
        }
        className={data.part_id ? CITATION_PANEL_CLASS : undefined}
        source={previewSource}
        pageInfo={pageInfo}
        clickable
        onViewSourceClick={handleViewSourceClick}
        triggerClickOpensSource
        viewSourceLabel="View full source"
        onTriggerHover={prefetchPreview}
        onOpen={loadPreview}
      >
        <button
          className="inline-flex items-center text-content-default text-xs bg-blue-200 rounded-full px-2 h-full align-top hover:bg-blue-100 max-w-[180px]"
          type="button"
        >
          {!hideTitle && <span className="truncate">{data.title}</span>}
          {pillPage != null && pillPage !== '' && (
            <span className="shrink-0">
              {!hideTitle && ', '}p. {pillPage}
            </span>
          )}
        </button>
      </SourcePopover>
    </>
  );
}

export function WebCitation({ data }: { data: WebSource }) {
  const posthog = usePostHog();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    posthog?.capture('chat_web_citation_clicked', { url: data.url, title: data.title });
    window.open(data.url, '_blank', 'noopener,noreferrer');
  };

  let hostname = '';
  try {
    hostname = new URL(data.url).hostname;
  } catch {
    hostname = data.url;
  }
  const displayTitle = data.title || hostname;

  return (
    <button
      className="inline-flex items-center gap-1 text-content-default text-xs bg-gray-200 rounded-full px-2 h-5 align-top hover:bg-gray-300 max-w-[200px] ml-0.5"
      onClick={handleClick}
    >
      <GlobeSimpleIcon size={12} weight="light" className="flex-shrink-0 text-content-subtlest" />
      <span className="truncate">{displayTitle}</span>
      <ArrowSquareOutIcon size={10} weight="light" className="flex-shrink-0 text-content-subtlest" />
    </button>
  );
}

export function SpreadsheetCitation({
  data,
  onViewSourceClick,
  hideTitle = false
}: {
  data: SpreadsheetSource;
  onViewSourceClick: (source: SpreadsheetSource) => void;
  hideTitle?: boolean;
}) {
  const posthog = usePostHog();
  const handleViewSourceClick = () => {
    posthog?.capture('chat_footnote_clicked', {
      citationType: 'spreadsheet',
      documentId: data.document_id,
      documentTitle: data.document_title,
      sheetName: data.sheet_name,
      cellRange: data.cell_range
    });
    onViewSourceClick(data);
  };

  return (
    <SourcePopover
      content="A preview isn't available for spreadsheet citations. Open the full source to view the cited cells."
      contentClassName="!text-content-subtlest italic"
      source={{ title: data.document_title }}
      pageInfo={`${data.sheet_name}!${data.cell_range}`}
      clickable
      onViewSourceClick={handleViewSourceClick}
      triggerClickOpensSource
      viewSourceLabel="View full source"
    >
      <button
        className="inline-flex items-center text-content-default text-xs bg-blue-200 rounded-full px-2 h-full align-top hover:bg-blue-100 max-w-[240px]"
        title={`${data.document_title} — ${data.sheet_name}!${data.cell_range}`}
        type="button"
      >
        {!hideTitle && <span className="truncate">{data.document_title}</span>}
        <span className="shrink-0">
          {!hideTitle && ', '}
          {data.sheet_name}!{data.cell_range}
        </span>
      </button>
    </SourcePopover>
  );
}
